import type { CompletionSource } from "@codemirror/autocomplete";
import { Compartment, EditorSelection, EditorState, Text, Transaction, type Extension } from "@codemirror/state";
import { EditorView } from "@codemirror/view";

import { clearNote, codeNotes, showNote } from "./code-note.ts";
import { keyLabel } from "./commands.ts";
import {
    cmakeLanguage,
    detectTabs,
    editorExtensions,
    indentation,
    languageFor,
    manifestLanguage,
} from "./editor-setup.ts";
import type { EditorLocation } from "./history.ts";
import { fileIcon } from "./icons.ts";
import { renderMarkdown } from "./markdown.ts";
import { applyEdits, type ReplaceEdit } from "./search.ts";
import { beside, isGrid, leaves, mapGrid, remove, renderGrid, split, type Grid, type Side } from "./split.ts";
import { wrapIndent } from "./wrap-indent.ts";
import type { ClangdLink } from "./lsp.ts";
import type { RuntimeLink } from "./runtime.ts";

export interface OpenOptions {
    line?: number;
    column?: number;
    /** A preview tab is replaced by the next preview until it is edited or pinned. */
    preview?: boolean;
    focus?: boolean;
    /** Show a Markdown file rendered, or as its source; unset keeps how its tab shows it. */
    rendered?: boolean;
    /** The heading to scroll a rendered Markdown file to, by its anchor. */
    anchor?: string;
}

/** The tabs in strip order, as the page stores them to open them again after a reload. */
export interface OpenEditors {
    /** Each tab's key: a file's guest path, or `view:` and the view's ID. */
    tabs: string[];
    active?: string;
    /** The preview tab, which the next preview replaces. */
    preview?: string;
    /** The tabs that show rendered Markdown or a file view in place of the text. */
    rendered: string[];
    /** The editor groups and their tabs, when the area is split; `tabs` lists them group by group. */
    groups?: Grid<SavedGroup>;
}

/** A group's tabs in strip order, and its shown tab. */
export interface SavedGroup {
    tabs: string[];
    active?: string;
}

/** The 1-based lines to show from a file's lines, or why there are none. */
export type PickLines = (lines: readonly string[]) => { first: number; last: number } | string;

/** What to do with edits to a file that changed on disk since it was read or saved. */
export type ConflictAnswer = "overwrite" | "revert" | undefined;

export interface EditorAreaHooks {
    saveFailed: (path: string, error: unknown) => void;
    /** An editor's text was written to its file, by a save or an auto-save. */
    saved?: (path: string) => void;
    saveConflict: (path: string) => Promise<ConflictAnswer>;
    /** Follow a link in rendered Markdown to another file or folder, and a `#fragment` in it. */
    followLink: (path: string, fragment: string) => void;
    /** Run an `idfweb:` link in rendered Markdown; `dir` is the document's folder. */
    action: (href: string, title: string | undefined, dir: string) => void;
    /** A command's keys, for the hints the group shows. */
    keys: (command: string) => readonly string[] | undefined;
}

/**
 * A tab with content of its own and no file behind it, such as the keyboard
 * shortcuts list. Opening a view with the `id` of an open one shows that tab.
 */
export interface ViewTab {
    id: string;
    title: string;
    /** A codicon name. */
    icon: string;
    /** Build the content when the tab opens; `dispose` runs when it closes. The `[autofocus]` element gets the focus. */
    create(): { element: HTMLElement; dispose?: () => void };
}

/**
 * Another way to show a file in place of its text, as rendered Markdown is:
 * the sdkconfig editor's form. The tab's action button switches between the
 * two, and the text is saved before the view is built.
 */
export interface FileView {
    matches(path: string): boolean;
    /** Whether a file shows the view when its tab opens. */
    byDefault: boolean;
    /** The action button's title and codicon while the text shows, and while the view does. */
    open: { title: string; icon: string };
    close: { title: string; icon: string };
    /** Build the view for the file; `saved` settles once the text is on disk. */
    create(path: string, saved: Promise<void>): { element: HTMLElement; dispose?: () => void };
    /** Called with the file's editor when the text is shown again. */
    textShown?(view: EditorView): void;
}

interface OpenView {
    kind: "view";
    spec: ViewTab;
    element: HTMLElement;
    dispose: (() => void) | undefined;
    tab: HTMLElement;
    group: Group;
}

type OpenTab = OpenEditor | OpenView;

interface OpenEditor {
    kind: "file";
    path: string;
    view: EditorView;
    /** The file's contents as last read or saved here. */
    saved: Text;
    /** The open save-conflict question, while it is unanswered. */
    conflict: Promise<void> | undefined;
    preview: boolean;
    readOnly: boolean;
    tab: HTMLElement;
    group: Group;
    lsp: Compartment;
    lspId: string | undefined;
    /** Whether the file indented with tabs when it was opened; undefined when it did not indent. */
    tabs: boolean | undefined;
    /** The rendered Markdown, while the tab shows it in place of the source. */
    rendered: HTMLElement | undefined;
    /** Object URLs of the rendered Markdown's images, revoked when it is drawn again or closed. */
    images: string[];
    /** The file view `rendered` holds, when it is not Markdown's. */
    fileView: { spec: FileView; dispose: (() => void) | undefined } | undefined;
}

/**
 * An editor group: a tab strip, breadcrumbs, and the stack that holds its
 * tabs' content. `keys` is the strip order; `recent` the order the group's
 * tabs were last shown, for the tab to show when one closes.
 */
interface Group {
    /** Names the group in a history location; not reused once the group closes. */
    id: string;
    element: HTMLElement;
    tabs: HTMLElement;
    renderToggle: HTMLButtonElement;
    splitButton: HTMLButtonElement;
    crumbs: HTMLElement;
    stack: HTMLElement;
    empty: HTMLElement;
    /** The part of the group a dragged tab would go to. */
    dropTarget: HTMLElement;
    keys: string[];
    recent: string[];
    active: OpenTab | undefined;
}

/** Paths under these are shown read-only: the bundled ESP-IDF tree, and files the page read from a board. */
const READ_ONLY = ["/idf/", "/device/"];

/** Saving after a pause in typing, as VS Code's `files.autoSave` setting "afterDelay" does. */
const AUTO_SAVE_MS = 1000;

/** The drag data type of a tab, whose value is the tab's key. */
const TAB_TYPE = "application/x-idfweb-tab";

/** The share of a group's width or height, from each edge, where a dropped tab splits the group. */
const EDGE = 0.3;

/** A phone-sized screen, where tabs are not dragged to split the area. */
const NARROW = "(max-width: 760px)";

/**
 * The editor area: editor groups laid out in a grid (`split.ts`), and one
 * CodeMirror view per open file. Each tab keeps its own view, so undo
 * history, scroll position and the language server's document stay with the
 * file while it is hidden. A file is open in one group at a time; moving its
 * tab moves the view. Calls without a group act on the active group, the one
 * last focused or shown. `#recent` keeps the order tabs were last shown, for
 * quick open.
 */
export class EditorArea {
    readonly element: HTMLElement;
    readonly #runtime: RuntimeLink;
    readonly #hooks: EditorAreaHooks;
    #clangd: ClangdLink | undefined;
    /** Language data for every editor; each language's applies only in its own files. */
    #languageData: Extension = [];
    #manifestData: Extension = [];
    /** Tabs of every group, by path for a file and `view:` and the id for a view. */
    readonly #open = new Map<string, OpenTab>();
    #recent: string[] = [];
    #grid: Grid<Group>;
    #group: Group;
    /** The key of the tab being dragged, which the drag events cannot read until the drop. */
    #dragged: string | undefined;
    readonly #narrow = window.matchMedia(NARROW);
    readonly #saveTimers = new Map<string, number>();
    readonly #listeners = new Set<() => void>();
    readonly #editListeners = new Set<(path: string) => void>();
    readonly #locationListeners = new Set<(location: EditorLocation, left: boolean) => void>();
    #groupIds = 0;
    /** One compartment for every editor's line wrapping, which is on or off for all of them. */
    readonly #wrap = new Compartment();
    #wordWrap = false;
    /** One compartment for every editor's indentation, reconfigured when a setting changes. */
    readonly #indent = new Compartment();
    #tabSize = 4;
    #insertSpaces = true;
    /** Views that show matching files in place of their text, besides rendered Markdown. */
    fileViews: FileView[] = [];
    /** Where a guest path is shown relative to, for breadcrumbs. */
    relativeTo: (path: string) => string[] = (path) => path.split("/").filter(Boolean);

    constructor(runtime: RuntimeLink, hooks: EditorAreaHooks) {
        this.#runtime = runtime;
        this.#hooks = hooks;
        this.element = div("wb-editor-area");
        this.#group = this.#newGroup();
        this.#grid = this.#group;
        this.#layout();
        this.#narrow.addEventListener("change", () => this.#render());
    }

    #newGroup(): Group {
        const element = div("wb-editor-group");
        element.tabIndex = -1;
        const tabs = div("wb-tabs");
        tabs.setAttribute("role", "tablist");
        // Editor actions sit at the end of the tab strip, as in VS Code.
        const tabBar = div("wb-tab-bar");
        const actions = div("wb-editor-actions");
        const renderToggle = document.createElement("button");
        renderToggle.type = "button";
        const splitButton = document.createElement("button");
        splitButton.type = "button";
        splitButton.className = "wb-split-button codicon codicon-split-horizontal";
        actions.append(renderToggle, splitButton);
        tabBar.append(tabs, actions);
        const crumbs = div("wb-breadcrumbs");
        const stack = div("wb-editor-stack");
        const empty = div("wb-editor-empty");
        const dropTarget = div("wb-drop-target");
        dropTarget.hidden = true;
        stack.append(empty, dropTarget);
        element.append(tabBar, crumbs, stack);
        const group: Group = {
            id: `g${++this.#groupIds}`,
            element,
            tabs,
            renderToggle,
            splitButton,
            crumbs,
            stack,
            empty,
            dropTarget,
            keys: [],
            recent: [],
            active: undefined,
        };
        renderToggle.addEventListener("click", () => {
            const file = group.active?.kind === "file" ? group.active : undefined;
            if (file !== undefined) this.setRendered(file.rendered === undefined, file.path);
        });
        // Alt+click splits down, as in VS Code.
        splitButton.addEventListener("click", (event) => this.#splitActive(group, event.altKey ? "down" : "right"));
        tabs.addEventListener(
            "wheel",
            (event) => {
                if (event.deltaY !== 0) tabs.scrollLeft += event.deltaY;
            },
            { passive: true },
        );
        const focus = () => {
            if (this.#group === group) return;
            this.#leave();
            this.#group = group;
            this.#located();
            this.#render();
        };
        element.addEventListener("focusin", focus);
        element.addEventListener("pointerdown", focus);
        this.#dropOnStrip(group);
        this.#dropOnStack(group);
        return group;
    }

    set clangd(clangd: ClangdLink) {
        this.#clangd = clangd;
    }

    /** Completion in CMake files, for editors opened from now on. */
    set cmakeCompletion(source: CompletionSource) {
        this.#languageData = cmakeLanguage.data.of({ autocomplete: source });
    }

    /** Completion in `idf_component.yml` files, for editors opened from now on. */
    set manifestCompletion(source: CompletionSource) {
        this.#manifestData = manifestLanguage.data.of({ autocomplete: source });
    }

    get activePath(): string | undefined {
        return this.#activeFile?.path;
    }

    get activeView(): EditorView | undefined {
        return this.#activeFile?.view;
    }

    /** Whether the active group shows a tab, a file's or a view's. */
    get hasActive(): boolean {
        return this.#group.active !== undefined;
    }

    /** Whether the active tab shows rendered Markdown. */
    get activeRendered(): boolean {
        return this.#activeFile?.rendered !== undefined;
    }

    /** Open paths, most recently shown first. */
    get recent(): string[] {
        return this.#recent.filter((key) => this.#file(key) !== undefined);
    }

    get openEditors(): OpenEditors {
        const files = this.#files();
        const saved = (group: Group): SavedGroup => ({
            tabs: [...group.keys],
            ...(group.active && { active: keyOf(group.active) }),
        });
        return {
            tabs: leaves(this.#grid).flatMap((group) => group.keys),
            active: this.#group.active && keyOf(this.#group.active),
            preview: files.find((editor) => editor.preview)?.path,
            rendered: files.filter((editor) => editor.rendered !== undefined).map((editor) => editor.path),
            ...(leaves(this.#grid).length > 1 && { groups: mapGrid(this.#grid, saved) }),
        };
    }

    /** How many groups the area has. */
    get groupCount(): number {
        return leaves(this.#grid).length;
    }

    /** Whether the active group has a tab to move into a new group, and another tab to keep. */
    get canSplit(): boolean {
        return this.#group.active !== undefined && this.#group.keys.length > 1;
    }

    /**
     * Replace the groups, which must have no tabs, with empty groups laid out
     * as `saved` is; an invalid layout leaves one group. Returns the saved
     * groups in the order `focusGroup` numbers the new ones.
     */
    arrange(saved: unknown): SavedGroup[] {
        if (this.#open.size > 0 || !isGrid(saved, isSavedGroup)) return [];
        const grid = mapGrid(saved, () => this.#newGroup());
        this.#grid = grid;
        this.#group = leaves(grid)[0]!;
        this.#layout();
        return leaves(saved);
    }

    /** Make the `index`-th group, in reading order, the one tabs open in. */
    focusGroup(index: number): void {
        const group = leaves(this.#grid)[index];
        if (group === undefined) return;
        this.#group = group;
        this.#render();
    }

    /** Close the groups that have no tabs, keeping one. */
    closeEmptyGroups(): void {
        for (const group of leaves(this.#grid)) if (group.keys.length === 0) this.#removeGroup(group);
    }

    /** Move the active tab into a new group on one side of its group. */
    split(side: Side = "right"): void {
        this.#splitActive(this.#group, side);
    }

    /**
     * Move the active tab into the next or previous group in reading order,
     * wrapping around; with one group, into a new group on the right.
     */
    moveToGroup(offset: number): void {
        const tab = this.#group.active;
        if (tab === undefined) return;
        const groups = leaves(this.#grid);
        if (groups.length === 1) return this.split(offset > 0 ? "right" : "left");
        const at = groups.indexOf(this.#group);
        this.#move(keyOf(tab), groups[(at + offset + groups.length) % groups.length]!);
    }

    /** Focus the next or previous group in reading order, wrapping around. */
    cycleGroup(offset: number): void {
        const groups = leaves(this.#grid);
        const at = groups.indexOf(this.#group);
        this.#leave();
        this.#group = groups[(at + offset + groups.length) % groups.length]!;
        this.#located();
        const tab = this.#group.active;
        if (tab === undefined) this.#group.element.focus();
        else this.#focus(tab);
        this.#render();
    }

    get dirty(): string[] {
        return this.#files()
            .filter((editor) => isDirty(editor))
            .map((editor) => editor.path);
    }

    get #activeFile(): OpenEditor | undefined {
        const active = this.#group.active;
        return active?.kind === "file" ? active : undefined;
    }

    #file(path: string): OpenEditor | undefined {
        const tab = this.#open.get(path);
        return tab?.kind === "file" ? tab : undefined;
    }

    #files(): OpenEditor[] {
        return [...this.#open.values()].filter((tab) => tab.kind === "file");
    }

    onChange(listener: () => void): void {
        this.#listeners.add(listener);
    }

    /** Called with the path on every change to an open file's text, typed or reloaded. */
    onEdit(listener: (path: string) => void): void {
        this.#editListeners.add(listener);
    }

    /**
     * Called with each location the person goes to: a tab shown, a group
     * focused, or a jump in a file's text; and with `left` set, with where a
     * tab was when another replaced it.
     */
    onLocation(listener: (location: EditorLocation, left: boolean) => void): void {
        this.#locationListeners.add(listener);
    }

    /** Where the active group's tab is now. */
    get location(): EditorLocation | undefined {
        return this.#group.active && locationOf(this.#group.active);
    }

    /**
     * Show a location again: an open tab in the group that has it now, a
     * closed file in its group, or in the active group once that one closed.
     * Resolves to false when the file is gone or the view closed.
     */
    async restore(location: EditorLocation): Promise<boolean> {
        if (location.key.startsWith("view:")) {
            const tab = this.#open.get(location.key);
            if (tab?.kind !== "view") return false;
            this.openView(tab.spec);
            return true;
        }
        const group = leaves(this.#grid).find((each) => each.id === location.group);
        if (!this.#open.has(location.key) && group !== undefined) this.#group = group;
        const rendered = location.scroll !== undefined;
        const { line, column } = location;
        const view = await this.#openFile(location.key, rendered ? { rendered } : { line, column, rendered });
        const root = this.#file(location.key)?.rendered;
        if (root !== undefined) root.scrollTop = location.scroll ?? 0;
        return view !== null;
    }

    async open(path: string, options: OpenOptions = {}): Promise<EditorView | null> {
        const view = await this.#openFile(path, options);
        if (view !== null) this.#located();
        return view;
    }

    async #openFile(path: string, options: OpenOptions): Promise<EditorView | null> {
        let editor = this.#file(path);
        if (editor === undefined) {
            let bytes: Uint8Array;
            try {
                bytes = await this.#runtime.fs.readFile(path);
            } catch {
                return null;
            }
            // Another open of the same path may have finished during the read.
            const opened = this.#file(path);
            editor = opened ?? this.#create(path, bytes, options.preview ?? false);
            if (opened === undefined && this.#fileView(path)?.byDefault === true)
                options = { rendered: true, ...options };
        } else if (options.preview === false) {
            editor.preview = false;
        }
        // A line is a place in the source.
        const rendered = options.line !== undefined ? false : options.rendered;
        if (rendered !== undefined) this.setRendered(rendered, path);
        this.#show(editor);
        if (options.line !== undefined) reveal(editor.view, options.line, options.column ?? 1);
        if (options.anchor !== undefined)
            editor.rendered?.querySelector(`[id="${CSS.escape(options.anchor)}"]`)?.scrollIntoView();
        if (options.focus !== false) (editor.rendered ?? editor.view).focus();
        return editor.view;
    }

    /**
     * Open a file's text at the lines `pick` chooses from its current text,
     * selected and marked, with `note` under them; a note in another file
     * closes. Resolves to why nothing is shown, if so.
     */
    async showLines(path: string, pick: PickLines, note: string): Promise<string | undefined> {
        const view = await this.#openFile(path, { preview: false, rendered: false });
        if (view === null) return `${path} is not here.`;
        const range = pick(view.state.doc.toJSON());
        if (typeof range === "string") {
            this.#located();
            return range;
        }
        for (const other of this.#files()) if (other.view !== view) clearNote(other.view);
        showNote(view, range.first, range.last, note);
        this.#located();
        return undefined;
    }

    /** Draw the hints again, after the commands' keys changed. */
    keysChanged(): void {
        this.#render();
    }

    /** Open a view's tab, or show it if it is open. */
    openView(spec: ViewTab, { focus = true } = {}): void {
        const key = viewKey(spec.id);
        let view = this.#open.get(key);
        if (view === undefined) {
            const { element, dispose } = spec.create();
            element.classList.add("wb-view");
            if (element.tabIndex < 0) element.tabIndex = -1;
            element.hidden = true;
            this.#group.stack.append(element);
            view = {
                kind: "view",
                spec,
                element,
                dispose,
                tab: this.#tab(key, icon(spec.icon, "wb-file-icon"), spec.title, spec.title),
                group: this.#group,
            };
            this.#open.set(key, view);
            this.#group.keys.push(key);
        }
        this.#show(view);
        this.#located();
        if (focus && view.kind === "view")
            (view.element.querySelector<HTMLElement>("[autofocus]") ?? view.element).focus();
    }

    /** Whether the file has a view besides its text: rendered Markdown or a file view. */
    hasRendering(path: string): boolean {
        return isMarkdown(path) || this.#fileView(path) !== undefined;
    }

    /** Show a file's tab rendered (Markdown, or its file view), or as its text. */
    setRendered(on: boolean, path = this.activePath): void {
        const editor = path === undefined ? undefined : this.#file(path);
        if (editor === undefined || !this.hasRendering(editor.path) || on === (editor.rendered !== undefined)) return;
        const focused = editor.view.hasFocus || editor.rendered?.contains(document.activeElement) === true;
        const spec = isMarkdown(editor.path) ? undefined : this.#fileView(editor.path);
        if (on && spec !== undefined) {
            const { element, dispose } = spec.create(editor.path, this.save(editor.path));
            element.classList.add("wb-view");
            editor.rendered = element;
            editor.fileView = { spec, dispose };
            editor.group.stack.append(element);
        } else if (on) {
            editor.rendered = div("wb-markdown");
            editor.rendered.tabIndex = 0;
            editor.group.stack.append(editor.rendered);
            this.#paint(editor);
        } else {
            this.#unpaint(editor);
            editor.view.requestMeasure();
            spec?.textShown?.(editor.view);
        }
        this.#display(editor, editor === editor.group.active);
        if (focused) (editor.rendered ?? editor.view).focus();
        this.#render();
    }

    /** The text of an open file, as edited, or of the file in the guest. */
    async text(path: string): Promise<string | null> {
        const editor = this.#file(path);
        if (editor !== undefined) return editor.view.state.doc.toString();
        try {
            return decode(await this.#runtime.fs.readFile(path));
        } catch {
            return null;
        }
    }

    /** Whether edits to the file are refused; a binary file is known only once it is open. */
    isReadOnly(path: string): boolean {
        return this.#file(path)?.readOnly ?? READ_ONLY.some((prefix) => path.startsWith(prefix));
    }

    /**
     * Apply the edits whose text is still as expected: in the editor as one
     * undoable change that is saved as typing is, or on disk when the file is
     * not open. Resolves to how many were applied.
     */
    async replace(path: string, edits: readonly ReplaceEdit[]): Promise<number> {
        if (this.isReadOnly(path)) return 0;
        const editor = this.#file(path);
        if (editor !== undefined) {
            const doc = editor.view.state.doc;
            const { applied } = applyEdits(doc.toString(), edits);
            if (applied.length === 0) return 0;
            editor.view.dispatch({
                changes: applied.map(({ from, to, insert }) => ({ from, to, insert })),
                userEvent: "input.replace",
            });
            return applied.length;
        }
        const before = await this.#read(path);
        if (before === null) return 0;
        const { text, applied } = applyEdits(before, edits);
        if (applied.length === 0) return 0;
        // Opened during the read: the edits go through the editor instead.
        if (this.#open.has(path)) return this.replace(path, edits);
        await this.#runtime.fs.writeFile(path, new TextEncoder().encode(text), { create: false, overwrite: true });
        return applied.length;
    }

    /**
     * Save the edits, unless the file changed on disk since it was read or
     * saved here: then ask whether to overwrite it or revert to it, and write
     * nothing until the answer. A dismissed question leaves the edits unsaved.
     */
    async save(path = this.activePath): Promise<void> {
        const editor = path === undefined ? undefined : this.#file(path);
        if (editor === undefined || editor.readOnly) return;
        clearTimeout(this.#saveTimers.get(editor.path));
        this.#saveTimers.delete(editor.path);
        if (editor.conflict !== undefined) return editor.conflict;
        if (!isDirty(editor)) return;
        const onDisk = await this.#read(editor.path);
        // Compared after the read: a save that finished during it moved `saved` to what it wrote.
        if (editor.conflict !== undefined) return editor.conflict;
        if (onDisk !== null && onDisk !== editor.saved.toString()) {
            editor.conflict = this.#resolve(editor).finally(() => (editor.conflict = undefined));
            return editor.conflict;
        }
        await this.#write(editor);
    }

    async saveAll(): Promise<void> {
        await Promise.all(this.#files().map((editor) => this.save(editor.path)));
    }

    /**
     * Close a tab, by a file's path or a view's key (the active tab by default);
     * `save: false` drops a file's unsaved edits, for a file that is gone.
     */
    close(key = this.#group.active && keyOf(this.#group.active), { save = true } = {}): void {
        this.#close(key, save, true);
    }

    /** Close a tab; `prune` also closes its group if that was the group's last tab and another group is left. */
    #close(key: string | undefined, save: boolean, prune: boolean): void {
        const tab = key === undefined ? undefined : this.#open.get(key);
        if (tab === undefined || key === undefined) return;
        if (tab.kind === "file") {
            clearTimeout(this.#saveTimers.get(tab.path));
            this.#saveTimers.delete(tab.path);
            if (save && isDirty(tab) && !tab.readOnly) this.#saveInBackground(tab.path);
            tab.view.destroy();
            tab.view.dom.remove();
            this.#unpaint(tab);
        } else {
            tab.dispose?.();
            tab.element.remove();
        }
        this.#open.delete(key);
        this.#recent = this.#recent.filter((k) => k !== key);
        this.#detach(tab);
        if (prune && tab.group.keys.length === 0) this.#removeGroup(tab.group);
        // The active group shows the next tab, or the group closed and another became active.
        if (tab.group === this.#group || !leaves(this.#grid).includes(tab.group)) this.#located();
        this.#render();
    }

    /** Close every tab, and every group but one. */
    closeAll(options?: { save?: boolean }): void {
        for (const path of [...this.#open.keys()]) this.close(path, options);
    }

    /** Close the tabs of `path` and of every file under it. */
    closeTree(path: string, options?: { save?: boolean }): void {
        for (const open of [...this.#open.keys()]) {
            if (open === path || open.startsWith(`${path}/`)) this.close(open, options);
        }
    }

    /** Show the tab `offset` places along the active group's strip, wrapping around. */
    cycle(offset: number): void {
        const keys = this.#group.keys;
        if (keys.length === 0) return;
        const active = this.#group.active;
        const at = active === undefined ? 0 : keys.indexOf(keyOf(active));
        this.#activate(keys[(at + offset + keys.length) % keys.length]!);
    }

    /** Reload files that changed in the guest and have no unsaved edits here. */
    async reload(paths: string[]): Promise<void> {
        for (const path of paths) {
            const editor = this.#file(path);
            if (editor === undefined || isDirty(editor)) continue;
            const before = editor.view.state.doc;
            let text: string;
            try {
                text = decode(await this.#runtime.fs.readFile(path));
            } catch {
                continue;
            }
            // Typing during the read makes the edits newer than the file.
            if (this.#open.get(path) !== editor || !editor.view.state.doc.eq(before)) continue;
            if (text === before.toString()) continue;
            editor.view.dispatch({
                changes: { from: 0, to: editor.view.state.doc.length, insert: text },
                annotations: Transaction.remote.of(true),
            });
            editor.saved = editor.view.state.doc;
            this.#render();
        }
    }

    /** Whether long lines wrap at the editor's width, in every editor. */
    get wordWrap(): boolean {
        return this.#wordWrap;
    }

    set wordWrap(on: boolean) {
        this.#wordWrap = on;
        for (const editor of this.#files()) {
            editor.view.dispatch({ effects: this.#wrap.reconfigure(wrapping(on)) });
        }
    }

    /** Columns between tab stops, in every editor. */
    get tabSize(): number {
        return this.#tabSize;
    }

    set tabSize(size: number) {
        this.#tabSize = size;
        this.#reindent();
    }

    /** Whether Tab and new indentation use spaces in a file that does not already indent with tabs or spaces. */
    get insertSpaces(): boolean {
        return this.#insertSpaces;
    }

    set insertSpaces(on: boolean) {
        this.#insertSpaces = on;
        this.#reindent();
    }

    #indentation(tabs: boolean | undefined): Extension {
        return indentation(this.#tabSize, tabs ?? !this.#insertSpaces);
    }

    #reindent(): void {
        for (const editor of this.#files()) {
            editor.view.dispatch({ effects: this.#indent.reconfigure(this.#indentation(editor.tabs)) });
        }
    }

    /** Take the language server's current extension into every C and C++ editor, or none. */
    reconfigureLanguageServer(attach = true): void {
        for (const editor of this.#files()) {
            if (editor.lspId === undefined || this.#clangd === undefined) continue;
            const extension = attach ? this.#clangd.extension(editor.path, editor.lspId) : [];
            editor.view.dispatch({ effects: editor.lsp.reconfigure(extension) });
        }
    }

    #create(path: string, bytes: Uint8Array, preview: boolean): OpenEditor {
        // One preview tab at a time: a new preview replaces the old one. The
        // group it opens in stays, even if the old one was its only tab.
        if (preview) {
            for (const other of this.#files()) {
                if (other.preview && !isDirty(other)) this.#close(other.path, true, other.group !== this.#group);
            }
        }
        const group = this.#group;
        const binary = bytes.subarray(0, 8000).includes(0);
        const text = binary ? "(binary file, not shown)" : decode(bytes);
        const readOnly = binary || READ_ONLY.some((prefix) => path.startsWith(prefix));
        const { support, lspId } = languageFor(path);
        const lsp = new Compartment();
        const lspExtension =
            lspId !== undefined && this.#clangd !== undefined ? this.#clangd.extension(path, lspId) : [];
        const doc = Text.of(text.split(/\r\n?|\n/));
        const tabs = detectTabs(doc);
        const state = EditorState.create({
            doc,
            extensions: [
                editorExtensions(),
                support ?? [],
                this.#languageData,
                this.#manifestData,
                lsp.of(lspExtension),
                this.#wrap.of(wrapping(this.#wordWrap)),
                this.#indent.of(this.#indentation(tabs)),
                EditorState.readOnly.of(readOnly),
                codeNotes,
                EditorView.updateListener.of((update) => {
                    if (
                        update.transactions.some((tr) => tr.isUserEvent("select")) &&
                        this.#activeFile === this.#file(path)
                    )
                        this.#located();
                    if (!update.docChanged) return;
                    for (const listener of this.#editListeners) listener(path);
                    const editor = this.#file(path);
                    if (editor?.rendered !== undefined && editor.fileView === undefined) this.#paint(editor);
                    if (update.transactions.some((tr) => tr.annotation(Transaction.remote))) return;
                    this.#edited(path);
                }),
            ],
        });
        const view = new EditorView({ state });
        view.dom.hidden = true;
        group.stack.append(view.dom);
        const tab = this.#tab(path, fileIcon(path), path.slice(path.lastIndexOf("/") + 1), path);
        tab.addEventListener("dblclick", () => void this.open(path, { preview: false }));
        const editor: OpenEditor = {
            kind: "file",
            path,
            view,
            saved: state.doc,
            conflict: undefined,
            preview,
            readOnly,
            tab,
            group,
            lsp,
            lspId,
            tabs,
            rendered: undefined,
            images: [],
            fileView: undefined,
        };
        this.#open.set(path, editor);
        group.keys.push(path);
        return editor;
    }

    #edited(path: string): void {
        const editor = this.#file(path);
        if (editor === undefined) return;
        editor.preview = false;
        clearTimeout(this.#saveTimers.get(path));
        this.#saveTimers.set(
            path,
            window.setTimeout(() => this.#saveInBackground(path), AUTO_SAVE_MS),
        );
        this.#render();
    }

    #saveInBackground(path: string): void {
        this.save(path).catch((e: unknown) => this.#hooks.saveFailed(path, e));
    }

    async #write(editor: OpenEditor): Promise<void> {
        const doc = editor.view.state.doc;
        await this.#runtime.fs.writeFile(editor.path, new TextEncoder().encode(doc.toString()), {
            create: true,
            overwrite: true,
        });
        editor.saved = doc;
        this.#render();
        this.#hooks.saved?.(editor.path);
    }

    async #resolve(editor: OpenEditor): Promise<void> {
        const answer = await this.#hooks.saveConflict(editor.path);
        if (answer === "overwrite") return this.#write(editor);
        // A closed tab has nothing to revert.
        if (answer !== "revert" || this.#open.get(editor.path) !== editor) return;
        const text = await this.#read(editor.path);
        if (text === null || this.#open.get(editor.path) !== editor) return;
        // Not an edit to save, but a step in the undo history: undoing it brings the edits back.
        editor.view.dispatch({
            changes: { from: 0, to: editor.view.state.doc.length, insert: text },
            annotations: Transaction.remote.of(true),
        });
        editor.saved = editor.view.state.doc;
        this.#render();
    }

    async #read(path: string): Promise<string | null> {
        try {
            return decode(await this.#runtime.fs.readFile(path));
        } catch {
            return null;
        }
    }

    /** Draw the file's rendered Markdown again from its current text, keeping the scroll position. */
    #paint(editor: OpenEditor): void {
        const root = editor.rendered!;
        const top = root.scrollTop;
        for (const url of editor.images.splice(0)) URL.revokeObjectURL(url);
        const dir = editor.path.slice(0, editor.path.lastIndexOf("/"));
        root.replaceChildren(
            renderMarkdown(editor.view.state.doc.toString(), {
                link: (href) => {
                    const hash = href.indexOf("#");
                    const target = hash < 0 ? href : href.slice(0, hash);
                    const fragment = hash < 0 ? "" : decodeLink(href.slice(hash + 1));
                    if (target === "") root.querySelector(`[id="${CSS.escape(fragment)}"]`)?.scrollIntoView();
                    else this.#hooks.followLink(resolve(dir, target), fragment);
                },
                action: (href, title) => this.#hooks.action(href, title, dir),
                image: async (src) => {
                    const path = resolve(dir, src.replace(/[?#].*$/, ""));
                    const bytes = await this.#runtime.fs.readFile(path).catch(() => undefined);
                    if (bytes === undefined || this.#open.get(editor.path) !== editor) return undefined;
                    const type = IMAGE_TYPES[path.slice(path.lastIndexOf(".") + 1).toLowerCase()] ?? "";
                    const url = URL.createObjectURL(new Blob([bytes as Uint8Array<ArrayBuffer>], { type }));
                    editor.images.push(url);
                    return url;
                },
            }),
        );
        root.scrollTop = top;
    }

    #fileView(path: string): FileView | undefined {
        return this.fileViews.find((view) => view.matches(path));
    }

    #unpaint(editor: OpenEditor): void {
        editor.fileView?.dispose?.();
        editor.fileView = undefined;
        for (const url of editor.images.splice(0)) URL.revokeObjectURL(url);
        editor.rendered?.remove();
        editor.rendered = undefined;
    }

    /** Show or hide a tab's content: a view's element, a file's rendered Markdown if it has any, else its editor. */
    #display(tab: OpenTab, shown: boolean): void {
        if (tab.kind === "view") {
            tab.element.hidden = !shown;
            return;
        }
        tab.view.dom.hidden = !shown || tab.rendered !== undefined;
        if (tab.rendered !== undefined) tab.rendered.hidden = !shown;
    }

    /** Tell the listeners where the active group's tab is, as a place the person went to. */
    #located(): void {
        const location = this.location;
        if (location !== undefined) for (const listener of this.#locationListeners) listener(location, false);
    }

    /** Tell the listeners where the active group's tab is, before another tab or group replaces it. */
    #leave(tab = this.#group.active): void {
        if (tab === undefined) return;
        const location = locationOf(tab);
        for (const listener of this.#locationListeners) listener(location, true);
    }

    /** Show a tab in its group, and make that group the active one. */
    #show(tab: OpenTab): void {
        if (this.#group !== tab.group) this.#leave();
        this.#reveal(tab);
        if (this.#group !== tab.group) {
            this.#group = tab.group;
            this.#render();
        }
    }

    /** Show a tab in its group. */
    #reveal(tab: OpenTab): void {
        const { group } = tab;
        if (group.active !== tab) {
            if (group.active !== undefined) {
                this.#leave(group.active);
                this.#display(group.active, false);
            }
            this.#display(tab, true);
            group.active = tab;
            // The view measures itself when it becomes visible.
            if (tab.kind === "file") tab.view.requestMeasure();
        }
        const key = keyOf(tab);
        group.recent = [key, ...group.recent.filter((k) => k !== key)];
        this.#recent = [key, ...this.#recent.filter((k) => k !== key)];
        this.#render();
    }

    #activate(key: string): void {
        const tab = this.#open.get(key);
        if (tab?.kind === "file") void this.open(tab.path);
        else if (tab !== undefined) this.openView(tab.spec);
    }

    #focus(tab: OpenTab): void {
        if (tab.kind === "view") (tab.element.querySelector<HTMLElement>("[autofocus]") ?? tab.element).focus();
        else (tab.rendered ?? tab.view).focus();
    }

    /** Take a tab out of its group's strip, and show the group's previous tab if it was the shown one. */
    #detach(tab: OpenTab): void {
        const { group } = tab;
        const key = keyOf(tab);
        group.keys = group.keys.filter((k) => k !== key);
        group.recent = group.recent.filter((k) => k !== key);
        if (group.active !== tab) return;
        this.#display(tab, false);
        group.active = undefined;
        const next = group.recent[0];
        if (next !== undefined) this.#reveal(this.#open.get(next)!);
    }

    /**
     * Move a tab into a group's strip before the tab at `index`, or after the
     * group's shown tab, and show it there. A group left without tabs closes.
     */
    #move(key: string, target: Group, index?: number, heirs: readonly Group[] = []): void {
        const tab = this.#open.get(key);
        if (tab === undefined) return;
        const from = tab.group;
        if (from === target) {
            if (index !== undefined) {
                const keys = target.keys.filter((k) => k !== key);
                const at = target.keys.indexOf(key) < index ? index - 1 : index;
                keys.splice(at, 0, key);
                target.keys = keys;
            }
            this.#show(tab);
            this.#focus(tab);
            return;
        }
        const restore = keepScroll(tab);
        // A moved tab is kept, as one that was edited is.
        if (tab.kind === "file") tab.preview = false;
        this.#detach(tab);
        for (const element of contentOf(tab)) target.stack.append(element);
        tab.group = target;
        const at =
            index ?? (target.active === undefined ? target.keys.length : target.keys.indexOf(keyOf(target.active)) + 1);
        target.keys.splice(at, 0, key);
        this.#show(tab);
        restore();
        this.#focus(tab);
        if (from.keys.length === 0) this.#removeGroup(from, heirs);
        this.#located();
        this.#render();
    }

    /** Move a group's shown tab into a new group on one side of it, if the group has another tab to keep. */
    #splitActive(group: Group, side: Side): void {
        const tab = group.active;
        if (tab === undefined || group.keys.length < 2) return;
        this.#splitTo(keyOf(tab), group, side);
    }

    #splitTo(key: string, target: Group, side: Side): void {
        const added = this.#newGroup();
        this.#grid = split(this.#grid, target, added, side);
        this.#layout();
        // A group the tab leaves empty gives its space to both halves of the split.
        this.#move(key, added, undefined, [added, target]);
    }

    /** Close a group that has no tabs, unless it is the last one; its space goes to a neighbour. */
    #removeGroup(group: Group, heirs: readonly Group[] = []): void {
        const groups = leaves(this.#grid);
        const at = groups.indexOf(group);
        if (groups.length < 2 || at < 0 || group.keys.length > 0) return;
        this.#grid = remove(this.#grid, group, heirs);
        if (this.#group === group) this.#group = groups[at > 0 ? at - 1 : 1]!;
        this.#layout();
        this.#render();
    }

    /**
     * Put the groups' elements in place for the grid. Moving an element in the
     * document resets its scroll position, so each shown tab's is kept.
     */
    #layout(): void {
        const restore = leaves(this.#grid).flatMap((group) => (group.active ? [keepScroll(group.active)] : []));
        const focused = this.element.contains(document.activeElement) ? document.activeElement : null;
        const root = renderGrid(
            this.#grid,
            (group) => group.element,
            () => this.#emit(),
        );
        root.style.flex = "1 1 0px";
        this.element.replaceChildren(root);
        this.element.classList.toggle("wb-split-area", leaves(this.#grid).length > 1);
        for (const done of restore) done();
        if (focused instanceof HTMLElement) focused.focus({ preventScroll: true });
    }

    /** Tabs dropped on a strip move into it, before the tab under the pointer. */
    #dropOnStrip(group: Group): void {
        const clear = () => group.tabs.querySelectorAll(".wb-drop-before, .wb-drop-after").forEach(unmark);
        const place = (event: DragEvent) => {
            const tabs = Array.from(group.tabs.querySelectorAll<HTMLElement>(".wb-tab"));
            const at = tabs.findIndex((tab) => {
                const box = tab.getBoundingClientRect();
                return event.clientX < box.left + box.width / 2;
            });
            return { tabs, index: at < 0 ? tabs.length : at };
        };
        group.tabs.addEventListener("dragover", (event) => {
            if (!event.dataTransfer?.types.includes(TAB_TYPE)) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
            const { tabs, index } = place(event);
            clear();
            if (index < tabs.length) tabs[index]!.classList.add("wb-drop-before");
            else tabs.at(-1)?.classList.add("wb-drop-after");
        });
        group.tabs.addEventListener("dragleave", clear);
        group.tabs.addEventListener("drop", (event) => {
            if (!event.dataTransfer?.types.includes(TAB_TYPE)) return;
            event.preventDefault();
            clear();
            const key = event.dataTransfer.getData(TAB_TYPE);
            if (this.#open.has(key)) this.#move(key, group, place(event).index);
        });
    }

    /**
     * A tab dropped near an edge of a group's content splits the group, and
     * one dropped in the middle moves into it, as in VS Code. The listeners
     * run in the capture phase, before CodeMirror's own drop handling.
     */
    #dropOnStack(group: Group): void {
        const where = (event: DragEvent): Side | "center" | undefined => {
            const tab = this.#dragged === undefined ? undefined : this.#open.get(this.#dragged);
            if (tab === undefined) return undefined;
            const box = group.stack.getBoundingClientRect();
            const x = (event.clientX - box.left) / box.width;
            const y = (event.clientY - box.top) / box.height;
            const edges: [Side, number][] = [
                ["left", x],
                ["right", 1 - x],
                ["up", y],
                ["down", 1 - y],
            ];
            const [side, distance] = edges.reduce((a, b) => (b[1] < a[1] ? b : a));
            // A group's only tab cannot leave it for a split of the same group.
            if (tab.group === group) return distance < EDGE && group.keys.length > 1 ? side : undefined;
            if (distance >= EDGE) return "center";
            // The tab's own group, already on that side, would close and leave the layout as it was.
            return tab.group.keys.length === 1 && beside(this.#grid, group, side) === tab.group ? undefined : side;
        };
        const hide = () => (group.dropTarget.hidden = true);
        group.stack.addEventListener(
            "dragover",
            (event) => {
                if (!event.dataTransfer?.types.includes(TAB_TYPE)) return;
                event.preventDefault();
                event.stopPropagation();
                const zone = where(event);
                event.dataTransfer.dropEffect = zone === undefined ? "none" : "move";
                group.dropTarget.hidden = zone === undefined;
                if (zone !== undefined) group.dropTarget.dataset.zone = zone;
            },
            true,
        );
        group.stack.addEventListener(
            "dragleave",
            (event) => {
                if (!group.stack.contains(event.relatedTarget as Node | null)) hide();
            },
            true,
        );
        group.stack.addEventListener(
            "drop",
            (event) => {
                if (!event.dataTransfer?.types.includes(TAB_TYPE)) return;
                event.preventDefault();
                event.stopPropagation();
                hide();
                const zone = where(event);
                const key = event.dataTransfer.getData(TAB_TYPE);
                if (zone === undefined || !this.#open.has(key)) return;
                if (zone === "center") this.#move(key, group);
                else this.#splitTo(key, group, zone);
            },
            true,
        );
    }

    #tab(key: string, icon: HTMLElement, text: string, title: string): HTMLElement {
        const tab = div("wb-tab");
        tab.setAttribute("role", "tab");
        tab.title = title;
        const label = document.createElement("span");
        label.className = "wb-tab-label";
        label.textContent = text;
        const close = document.createElement("button");
        close.type = "button";
        close.className = "wb-tab-close codicon codicon-close";
        close.title = "Close";
        close.addEventListener("click", (event) => {
            event.stopPropagation();
            this.close(key);
        });
        tab.append(icon, label, close);
        tab.addEventListener("click", () => this.#activate(key));
        tab.addEventListener("auxclick", (event) => {
            if (event.button === 1) this.close(key);
        });
        tab.addEventListener("dragstart", (event) => {
            event.dataTransfer?.setData(TAB_TYPE, key);
            if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
            this.#dragged = key;
        });
        tab.addEventListener("dragend", () => {
            this.#dragged = undefined;
            for (const group of leaves(this.#grid)) group.dropTarget.hidden = true;
        });
        return tab;
    }

    #emit(): void {
        for (const listener of this.#listeners) listener();
    }

    #render(): void {
        const split = leaves(this.#grid).length > 1;
        for (const group of leaves(this.#grid)) this.#renderGroup(group, split);
        this.#emit();
    }

    #renderGroup(group: Group, split: boolean): void {
        group.element.classList.toggle("active", group === this.#group);
        const tabs = group.keys.map((key) => {
            const open = this.#open.get(key)!;
            const { tab } = open;
            const active = open === group.active;
            tab.classList.toggle("active", active);
            tab.setAttribute("aria-selected", String(active));
            tab.draggable = !this.#narrow.matches;
            if (open.kind === "file") {
                tab.classList.toggle("preview", open.preview);
                tab.classList.toggle("dirty", isDirty(open));
                tab.classList.toggle("read-only", open.readOnly);
            }
            return tab;
        });
        // Putting the same tabs back would cancel a click that the group's pointerdown drew them for.
        const children = group.tabs.children;
        if (tabs.length !== children.length || tabs.some((tab, i) => children[i] !== tab))
            group.tabs.replaceChildren(...tabs);
        group.active?.tab.scrollIntoView({ block: "nearest", inline: "nearest" });
        // A group of a split is never empty for long: it closes with its last tab.
        group.empty.hidden = group.active !== undefined || split;
        if (!group.empty.hidden) group.empty.replaceChildren(emptyHint(this.#hooks.keys));
        const file = group.active?.kind === "file" ? group.active : undefined;
        const rendered = file?.rendered !== undefined;
        const previewKey = this.#hooks.keys("markdown.showPreview")?.[0];
        const fileView = file === undefined || isMarkdown(file.path) ? undefined : this.#fileView(file.path);
        const toggle = group.renderToggle;
        toggle.hidden = file === undefined || !this.hasRendering(file.path);
        if (fileView !== undefined) {
            const action = rendered ? fileView.close : fileView.open;
            toggle.className = `codicon codicon-${action.icon}`;
            toggle.title = action.title;
        } else {
            toggle.className = `codicon codicon-${rendered ? "go-to-file" : "open-preview"}`;
            toggle.title = rendered
                ? "Open Source"
                : previewKey === undefined
                  ? "Open Preview"
                  : `Open Preview (${keyLabel(previewKey)})`;
        }
        // The icon's glyph would otherwise be the button's name.
        toggle.setAttribute("aria-label", toggle.title);
        const splitKey = this.#hooks.keys("workbench.action.splitEditor")?.[0];
        group.splitButton.title =
            splitKey === undefined ? "Split Editor Right" : `Split Editor Right (${keyLabel(splitKey)})`;
        group.splitButton.setAttribute("aria-label", group.splitButton.title);
        group.splitButton.disabled = group.keys.length < 2;
        group.crumbs.replaceChildren();
        if (group.active?.kind === "view") {
            const crumb = document.createElement("span");
            crumb.className = "wb-crumb";
            crumb.append(icon(group.active.spec.icon, "wb-file-icon"), group.active.spec.title);
            group.crumbs.append(crumb);
        } else if (file !== undefined) {
            const parts = this.relativeTo(file.path);
            parts.forEach((part, i) => {
                if (i > 0) group.crumbs.append(icon("chevron-right", "wb-crumb-sep"));
                const crumb = document.createElement("span");
                crumb.className = "wb-crumb";
                if (i === parts.length - 1) crumb.append(fileIcon(file.path));
                crumb.append(part);
                group.crumbs.append(crumb);
            });
            if (file.readOnly) group.crumbs.append(icon("lock", "wb-crumb-lock"));
        }
    }
}

function isSavedGroup(value: unknown): value is SavedGroup {
    if (typeof value !== "object" || value === null) return false;
    const { tabs, active } = value as Partial<SavedGroup>;
    return (
        Array.isArray(tabs) &&
        tabs.every((tab) => typeof tab === "string") &&
        (active === undefined || typeof active === "string")
    );
}

/** The elements a tab shows in its group's stack. */
function contentOf(tab: OpenTab): HTMLElement[] {
    if (tab.kind === "view") return [tab.element];
    return tab.rendered === undefined ? [tab.view.dom] : [tab.view.dom, tab.rendered];
}

/** Take a tab's scroll position, and return a function that puts it back after its element moved. */
function keepScroll(tab: OpenTab): () => void {
    if (tab.kind === "file" && tab.rendered === undefined) {
        const snapshot = tab.view.scrollSnapshot();
        return () => tab.view.dispatch({ effects: snapshot });
    }
    const element = tab.kind === "file" ? tab.rendered! : tab.element;
    const { scrollTop, scrollLeft } = element;
    return () => element.scrollTo(scrollLeft, scrollTop);
}

function unmark(element: Element): void {
    element.classList.remove("wb-drop-before", "wb-drop-after");
}

const IMAGE_TYPES: Record<string, string> = {
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    webp: "image/webp",
    svg: "image/svg+xml",
};

export function isMarkdown(path: string): boolean {
    return /\.(md|markdown)$/i.test(path);
}

/** `relative` joined to the directory `dir`, with `.` and `..` resolved; `/`-prefixed paths are absolute. */
export function resolve(dir: string, relative: string): string {
    const parts = relative.startsWith("/") ? [] : dir.split("/").filter(Boolean);
    for (const part of decodeLink(relative).split("/")) {
        if (part === "..") parts.pop();
        else if (part !== "." && part !== "") parts.push(part);
    }
    return `/${parts.join("/")}`;
}

/** A percent-encoded link part, or the part as written when it is not valid. */
function decodeLink(part: string): string {
    try {
        return decodeURIComponent(part);
    } catch {
        return part;
    }
}

export function viewKey(id: string): string {
    return `view:${id}`;
}

/** A tab's key in the group: a file's path, which is absolute, or a view's `view:` key. */
function keyOf(tab: OpenTab): string {
    return tab.kind === "file" ? tab.path : viewKey(tab.spec.id);
}

/** Where a tab is: its group, and a file's cursor or how far its rendering is scrolled. */
function locationOf(tab: OpenTab): EditorLocation {
    const key = keyOf(tab);
    const group = tab.group.id;
    if (tab.kind === "view") return { key, group };
    if (tab.rendered !== undefined) return { key, group, scroll: tab.rendered.scrollTop };
    const head = tab.view.state.selection.main.head;
    const line = tab.view.state.doc.lineAt(head);
    return { key, group, line: line.number, column: head - line.from + 1 };
}

function isDirty(editor: OpenEditor): boolean {
    return !editor.readOnly && !editor.view.state.doc.eq(editor.saved);
}

function reveal(view: EditorView, line: number, column: number): void {
    const doc = view.state.doc;
    const at = doc.line(Math.max(1, Math.min(line, doc.lines)));
    const pos = Math.min(at.from + Math.max(0, column - 1), at.to);
    view.dispatch({
        selection: EditorSelection.cursor(pos),
        effects: EditorView.scrollIntoView(pos, { y: "center" }),
    });
}

function decode(bytes: Uint8Array): string {
    return new TextDecoder().decode(bytes);
}

function div(className: string): HTMLElement {
    const element = document.createElement("div");
    element.className = className;
    return element;
}

function icon(name: string, className = ""): HTMLElement {
    const element = document.createElement("span");
    element.className = `codicon codicon-${name} ${className}`;
    return element;
}

/**
 * A few commands and their first keys, for an empty editor area. The page
 * registers the project picker; without it, that row is left out.
 */
function emptyHint(keys: EditorAreaHooks["keys"]): HTMLElement {
    const rows = [
        ["Open a project to get started", "idfweb.openProject"],
        ["Show all commands", "workbench.action.showCommands"],
    ] as const;
    const list = document.createElement("dl");
    for (const [what, command] of rows) {
        const key = keys(command)?.[0];
        if (key === undefined) continue;
        const term = document.createElement("dt");
        term.textContent = what;
        const kbd = document.createElement("kbd");
        kbd.textContent = keyLabel(key);
        const detail = document.createElement("dd");
        detail.append(kbd);
        list.append(term, detail);
    }
    return list;
}

function wrapping(on: boolean) {
    return on ? [EditorView.lineWrapping, wrapIndent] : [];
}
